import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import { X } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { useSenderEmailStore } from '@/stores/senderEmailStore'
import { useScrollLock } from '@/lib/useScrollLock'
import { checkboxInputClass } from '@/lib/styleKit'
// Field chrome, the live-region class and the §16 dialog contract are shared
// with the Add modal — declared once there (see the note on `fieldLabel`),
// imported here so the two stay in lockstep.
import {
  fieldLabel, checkboxLabel, outcomeClass, useDialogChrome,
  type DialogOutcome,
} from './AddSenderEmailModal'
import type { SenderEmail } from '@/types/senderEmail'
import { parsePortInput, portInputProps } from './AddSenderEmailModal'

interface Props {
  sender: SenderEmail
  onClose: () => void
  onDirtyChange?: (dirty: boolean) => void
}

const EditSenderEmailModal: React.FC<Props> = ({ sender, onClose, onDirtyChange }) => {
  useScrollLock(true)
  const { updateSenderEmail } = useSenderEmailStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [outcome, setOutcome] = useState<DialogOutcome | null>(null)
  const [showDiscard, setShowDiscard] = useState(false)

  const surfaceRef = useRef<HTMLDivElement>(null)
  const firstFieldRef = useRef<HTMLInputElement>(null)
  const uid = useId()
  const titleId = `${uid}-title`
  const descId = `${uid}-desc`
  const fid = (name: string) => `${uid}-${name}`

  const [label, setLabel] = useState(sender.label)
  const [displayName, setDisplayName] = useState(sender.display_name || '')
  const [smtpServer, setSmtpServer] = useState(sender.smtp_server)
  const [smtpPort, setSmtpPort] = useState(sender.smtp_port)
  const [smtpUseTls, setSmtpUseTls] = useState(sender.smtp_use_tls)
  const [smtpUseSsl, setSmtpUseSsl] = useState(sender.smtp_use_ssl)
  const [imapServer, setImapServer] = useState(sender.imap_server || '')
  const [imapPort, setImapPort] = useState(sender.imap_port || 993)
  // Rows created before these columns existed read as SSL (the migration's
  // server_default), which is what every IMAP connection used to hardcode.
  const [imapUseSsl, setImapUseSsl] = useState(sender.imap_use_ssl ?? true)
  const [imapUseStarttls, setImapUseStarttls] = useState(sender.imap_use_starttls ?? false)
  const [monitoringEnabled, setMonitoringEnabled] = useState(sender.monitoring_enabled)
  const [newSmtpPassword, setNewSmtpPassword] = useState('')
  // Whether the stored password is a provider-issued app password. Editable
  // here because a mailbox can be moved onto one later (Google forces it once
  // 2-Step is switched on), and the stored answer is what shapes the error if
  // the credential is ever refused.
  const [usesAppPassword, setUsesAppPassword] = useState(sender.uses_app_password ?? false)

  const currentSnapshot = useMemo(
    () =>
      JSON.stringify([
        label, displayName, smtpServer, smtpPort, smtpUseTls, smtpUseSsl,
        imapServer, imapPort, monitoringEnabled, newSmtpPassword,
        imapUseSsl, imapUseStarttls, usesAppPassword,
      ]),
    [
      label, displayName, smtpServer, smtpPort, smtpUseTls, smtpUseSsl,
      imapServer, imapPort, monitoringEnabled, newSmtpPassword,
      imapUseSsl, imapUseStarttls, usesAppPassword,
    ],
  )
  // Snapshot of the row as it was opened (§18; `savedValues` idiom from
  // `CampaignDefaultsSection`). Seeded once on mount by `useRef`.
  const savedSnapshotRef = useRef(currentSnapshot)
  const isDirty = currentSnapshot !== savedSnapshotRef.current

  useEffect(() => {
    onDirtyChange?.(isDirty)
    return () => onDirtyChange?.(false)
  }, [isDirty, onDirtyChange])

  /**
   * Dismissal guard (§18). The backdrop stays inert — an outside click would
   * silently discard unsaved edits. Escape, the X and Cancel land here: a
   * pristine form closes at once, a dirty one goes through the confirmation.
   */
  const requestClose = () => {
    if (isSubmitting) return
    if (isDirty) {
      setShowDiscard(true)
      return
    }
    onClose()
  }

  const handleSubmit = async () => {
    // In-flight guard — the keyboard paths reach this directly and a
    // double-PATCH would fire two writes for one intent.
    if (isSubmitting) return
    setIsSubmitting(true)
    setOutcome({ text: 'Saving changes…' })
    try {
      const data: Record<string, any> = {
        label,
        display_name: displayName || undefined,
        smtp_server: smtpServer,
        smtp_port: smtpPort,
        smtp_use_tls: smtpUseTls,
        smtp_use_ssl: smtpUseSsl,
        imap_server: imapServer || undefined,
        imap_port: imapPort || undefined,
        imap_use_ssl: imapUseSsl,
        imap_use_starttls: imapUseStarttls,
        monitoring_enabled: monitoringEnabled,
        uses_app_password: usesAppPassword,
      }
      if (newSmtpPassword) {
        data.smtp_password = newSmtpPassword
      }
      await updateSenderEmail(sender.id, data)
      setOutcome({ text: 'Changes saved.' })
      onClose()
    } catch {
      // Store owns the toast; the live region owns the announcement.
      setOutcome({ text: 'Could not save the changes. Try again.', error: true })
    } finally {
      setIsSubmitting(false)
    }
  }

  useDialogChrome(surfaceRef, requestClose, firstFieldRef)

  if (typeof document === 'undefined') return null

  // Portalled to `document.body` (§29); `AnimatePresence` lives in
  // `SenderEmailsPage`, outside the conditional that mounts this component, so
  // the exit variant actually plays. PresenceContext travels the React tree, not
  // the DOM tree — the portalled surface still animates out.
  return createPortal(
    <>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md">
        {/* Backdrop — blur-only, no dark overlay (§45). No click-to-close: an
            outside click would silently discard unsaved edits. Close via
            Cancel, the X or Escape. */}
        <motion.div
          className="fixed top-0 left-0 w-screen h-screen"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          aria-hidden="true"
        />
        <motion.div
          ref={surfaceRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descId}
          // Focusable-but-not-tabbable so clicks on dead space stay inside the
          // dialog and the scoped keydown listener keeps seeing Escape/Tab.
          tabIndex={-1}
          className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl outline-none bg-card border border-border/60 shadow-2xl dark:bg-[#191a1b] dark:border-white/[0.08]"
          initial={{ scale: 0.96, opacity: 0, y: 8 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 8 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-5 border-b border-border/50 dark:border-white/[0.06]">
            <div className="min-w-0">
              <h2
                id={titleId}
                className="text-foreground dark:text-[#f7f8f8] truncate"
                style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.2px' }}
              >
                Edit Email Account
              </h2>
              <p id={descId} className="mt-0.5 text-[13px] text-muted-foreground dark:text-[#8a8f98] truncate">
                {sender.email_address}
              </p>
            </div>
            <button
              type="button"
              onClick={requestClose}
              aria-label="Close"
              className="h-8 w-8 -mr-1 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] transition-colors duration-150 flex-shrink-0"
            >
              <X className="h-4 w-4" weight="bold" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-4">
            <div>
              <label htmlFor={fid('label')} className={fieldLabel}>Label</label>
              <Input id={fid('label')} ref={firstFieldRef} value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>
            <div>
              <label htmlFor={fid('display-name')} className={fieldLabel}>Display Name</label>
              <Input id={fid('display-name')} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label htmlFor={fid('smtp-server')} className={fieldLabel}>SMTP Server</label>
                <Input id={fid('smtp-server')} value={smtpServer} onChange={(e) => setSmtpServer(e.target.value)} />
              </div>
              <div>
                <label htmlFor={fid('smtp-port')} className={fieldLabel}>Port</label>
                <Input id={fid('smtp-port')} {...portInputProps} value={smtpPort || ''} onChange={(e) => setSmtpPort(parsePortInput(e.target.value))} />
              </div>
            </div>
            {/* The state behind these survived 385c1d4 but the controls did not,
                so this form silently passed through whatever was already stored
                and there was no way to correct a mailbox on the wrong transport.
                Mutually exclusive; each moves the port to its convention. */}
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              <label className={checkboxLabel}>
                <input
                  type="checkbox"
                  checked={smtpUseTls}
                  onChange={(e) => {
                    setSmtpUseTls(e.target.checked)
                    if (e.target.checked) { setSmtpUseSsl(false); setSmtpPort(587) }
                  }}
                  className={checkboxInputClass}
                />
                Use STARTTLS (port 587)
              </label>
              <label className={checkboxLabel}>
                <input
                  type="checkbox"
                  checked={smtpUseSsl}
                  onChange={(e) => {
                    setSmtpUseSsl(e.target.checked)
                    if (e.target.checked) { setSmtpUseTls(false); setSmtpPort(465) }
                  }}
                  className={checkboxInputClass}
                />
                Use SSL (implicit TLS, port 465)
              </label>
            </div>
            <div>
              <label htmlFor={fid('smtp-password')} className={fieldLabel}>New SMTP Password</label>
              <Input
                id={fid('smtp-password')}
                type="password"
                value={newSmtpPassword}
                onChange={(e) => setNewSmtpPassword(e.target.value)}
                placeholder="Leave empty to keep current"
              />
              {/* One box for both protocols: an app password authenticates
                  SMTP and IMAP alike. See AddSenderEmailModal. */}
              <label className={`${checkboxLabel} mt-2.5`}>
                <input
                  type="checkbox"
                  checked={usesAppPassword}
                  onChange={(e) => setUsesAppPassword(e.target.checked)}
                  className={checkboxInputClass}
                />
                This is an app password
              </label>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2 pt-0.5">
              <label className={checkboxLabel}>
                <input
                  type="checkbox"
                  checked={smtpUseTls}
                  onChange={(e) => { setSmtpUseTls(e.target.checked); if (e.target.checked) setSmtpUseSsl(false) }}
                  className={checkboxInputClass}
                />
                TLS
              </label>
              <label className={checkboxLabel}>
                <input
                  type="checkbox"
                  checked={smtpUseSsl}
                  onChange={(e) => { setSmtpUseSsl(e.target.checked); if (e.target.checked) setSmtpUseTls(false) }}
                  className={checkboxInputClass}
                />
                SSL
              </label>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label htmlFor={fid('imap-server')} className={fieldLabel}>IMAP Server</label>
                <Input id={fid('imap-server')} value={imapServer} onChange={(e) => setImapServer(e.target.value)} placeholder={smtpServer} />
              </div>
              <div>
                <label htmlFor={fid('imap-port')} className={fieldLabel}>IMAP Port</label>
                <Input id={fid('imap-port')} {...portInputProps} value={imapPort || ''} onChange={(e) => setImapPort(parsePortInput(e.target.value))} />
              </div>
            </div>
            {/* Mutually exclusive, and each moves the port to its conventional
                value — 993 for implicit TLS, 143 for STARTTLS. That pairing is
                the thing people get wrong, and until these existed every IMAP
                connection was hardcoded to implicit TLS. */}
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              <label className={checkboxLabel}>
                <input
                  type="checkbox"
                  checked={imapUseSsl}
                  onChange={(e) => {
                    setImapUseSsl(e.target.checked)
                    if (e.target.checked) { setImapUseStarttls(false); setImapPort(993) }
                  }}
                  className={checkboxInputClass}
                />
                Use SSL (implicit TLS, port 993)
              </label>
              <label className={checkboxLabel}>
                <input
                  type="checkbox"
                  checked={imapUseStarttls}
                  onChange={(e) => {
                    setImapUseStarttls(e.target.checked)
                    if (e.target.checked) { setImapUseSsl(false); setImapPort(143) }
                  }}
                  className={checkboxInputClass}
                />
                Use STARTTLS (port 143)
              </label>
            </div>
            {/* Daily Send Limit is no longer user-editable and is no longer sent
                on save. The cap is enforced on every send, so it belongs to the
                backend: it is resolved from the chosen provider at create time
                (core/smtp_providers.py) and SenderEmailUpdate no longer accepts
                the field at all. The removed control was:

                  <label htmlFor={fid('daily-limit')}>Daily Send Limit</label>
                  <Input id={fid('daily-limit')} type="number"
                         value={dailySendLimit}
                         onChange={e => setDailySendLimit(Number(e.target.value))} />

                The warmup ramp still adjusts the stored value internally
                (services/warmup/service.py). */}
            <label className={checkboxLabel}>
              <input
                type="checkbox"
                checked={monitoringEnabled}
                onChange={(e) => setMonitoringEnabled(e.target.checked)}
                className={checkboxInputClass}
              />
              Enable mailbox monitoring
            </label>
          </div>

          {/* Outcome of the async save — announced. Present from first render so
              the live region exists before it fills. */}
          <p role="status" className={outcomeClass(outcome)}>{outcome?.text ?? ''}</p>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border/50 dark:border-white/[0.06]">
            <Button variant="outline" onClick={requestClose}>Cancel</Button>
            <Button onClick={handleSubmit} loading={isSubmitting}>
              Save Changes
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Discard guard for Escape / Cancel / X on a dirty form. Portals after
          this modal's own content, so it paints on top inside the same
          `z-[100]` modal band (§59). */}
      <ConfirmDialog
        open={showDiscard}
        title="Discard changes?"
        description="You have unsaved changes. Leaving now will discard them."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        tone="destructive"
        onConfirm={() => { setShowDiscard(false); onClose() }}
        onCancel={() => setShowDiscard(false)}
      />
    </>,
    document.body,
  )
}

export default EditSenderEmailModal
